/**
 * PR-11: GUARD CANONICO UNICO
 *
 * Cadena oficial de resolucion de acceso:
 * 1. Ruta publica -> permitir
 * 2. requiresAuth -> exigir sesion activa (authStore)
 * 3. guestOnly + autenticado -> redirigir a dashboard
 * 4. Validar allowedRoles / roles
 * 5. Redirigir a /access-denied si falla
 *
 * Fuente de verdad de sesion: authStore (Pinia)
 * NO usa localStorage directa, tokens hardcodeados, ni fetch manual.
 */

import type { RouteLocationNormalized } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

// P13-V8 (DEC-V8-01): AUTOMATIONS_ALLOWED_ROLES is retired with the /automations surface.

/**
 * Enterprise Agenda (P12 §6/§7) — fuente única de autoridad de rutas de agenda.
 * El sidebar importa ESTAS MISMAS constantes: visibilidad de navegación ===
 * autoridad de ruta. ALL7 = los siete roles canónicos.
 */
// MGI-PILOT-W2G · AUTORIDAD DE SUPERFICIE DE AGENDA EN EL PRIMER PILOTO.
// La Agenda NO se vende en el contrato del primer piloto. Esta es la UNICA fuente de admision de la
// superficie de Agenda entregada: la consumen tanto las rutas (admision directa via canonicalGuard)
// como la visibilidad de navegacion, de modo que no puede haber deriva entre "lo que se anuncia" y
// "lo que se puede abrir". La autoridad real sigue siendo del backend (perimetro W2-G en
// /api/agenda y /api/agenda-delegations); esto es verdad de superficie.
// Los conjuntos amplios de abajo se CONSERVAN: documentan la matriz aceptada para cuando la Agenda
// se comercialice y siguen fijados por su propia evidencia historica.
export const AGENDA_PILOT_SURFACE_ROLES = Object.freeze(['super_admin'])

export const AGENDA_ALL_ROLES = Object.freeze([
  'super_admin',
  'administration_manager',
  'group_manager',
  'field_supervisor',
  'department_head',
  'route_operator',
  'agency_employee',
])

/** COORD (P12 §5): matriz de coordinación — organized/team */
export const AGENDA_COORDINATION_ROLES = Object.freeze([
  'super_admin',
  'administration_manager',
  'group_manager',
  'field_supervisor',
  'department_head',
])

const CANONICAL_ROLES = new Set([
  'super_admin',
  'administration_manager',
  'group_manager',
  'field_supervisor',
  'department_head',
  'route_operator',
  'agency_employee',
])

function normalizeRole(role: string): string {
  return String(role || '').trim()
}

/**
 * P13-V7B — THE role rule of a route, as a pure function.
 *
 * `canonicalGuard` decides who may OPEN a route; every navigation surface decides what to SHOW. When
 * those two are written separately they drift: measured on the unrepaired tree, the super_admin sidebar
 * offered "Mapas Regionales" (a route that admits only `group_manager`), the reports centre offered a
 * group manager three super_admin pages, and a department head was handed "Ver en Mapa". Each of them
 * ended on `/access-denied`.
 *
 * This is the rule the guard applies, extracted so a menu can ask the SAME question before it draws an
 * item. It grants nothing: the guard still runs on every navigation and the backend is still the only
 * authority. A route with no role list admits any authenticated canonical role, exactly as before.
 */
export function isRoleAdmittedByRouteMeta(
  meta: Record<string, any> | undefined,
  role: string | null | undefined
): boolean {
  const allowedRoles: string[] | undefined = meta?.allowedRoles || meta?.roles
  if (!Array.isArray(allowedRoles) || allowedRoles.length === 0) return true
  const normalizedUser = normalizeRole(role || '')
  if (!CANONICAL_ROLES.has(normalizedUser)) return false
  return allowedRoles.map(normalizeRole).filter(r => CANONICAL_ROLES.has(r)).includes(normalizedUser)
}

export async function canonicalGuard(
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized
): Promise<string | undefined> {
  const authStore = useAuthStore()
  const meta = to.meta as Record<string, any>

  // P13-V7B (refutation): a URL that matches no record used to fall through as "public" (its meta
  // is empty) and render an empty router-view — a blank page with no way home and no sign-out, for
  // every role. It is sent to the not-found page, which names the session's own home. Decided here,
  // not by a catch-all record, so the route table still states exactly which paths exist.
  if (Array.isArray(to.matched) && to.matched.length === 0 && to.path !== '/not-found') {
    return '/not-found'
  }

  if (authStore.sessionAuthority === 'restoring') {
    await authStore.waitForSessionRestore()
  }

  if (!meta.requiresAuth) {
    if (meta.guestOnly && authStore.isAuthenticated) {
      return getRedirectForRole(authStore.userRole || '')
    }
    return undefined
  }

  if (!authStore.isAuthenticated) {
    return '/login'
  }

  const allowedRoles: string[] | undefined = meta.allowedRoles || meta.roles
  if (!isRoleAdmittedByRouteMeta(meta, authStore.userRole)) {
    console.warn(
      `[Guard] Acceso denegado a ${to.path}: rol "${authStore.userRole || ''}" no esta en [${(allowedRoles || []).join(', ')}]`
    )
    return '/access-denied'
  }

  return undefined
}

export function isPublicRoute(path: string): boolean {
  const publicPrefixes = [
    '/login', '/auth/', '/register', '/forgot-password',
    '/reset-password', '/error', '/access-denied', '/not-found'
  ]
  return publicPrefixes.some(prefix => path.startsWith(prefix))
}

export function getRedirectForRole(role: string): string {
  const redirects: Record<string, string> = {
    'super_admin': '/super-admin',
    'administration_manager': '/administration-manager',
    'group_manager': '/group-manager',
    'field_supervisor': '/field-supervisor',
    'department_head': '/department-head',
    'route_operator': '/route-operator',
    'agency_employee': '/agency-employee',
  }
  return redirects[normalizeRole(role)] || '/dashboard'
}

export default { canonicalGuard, isPublicRoute, getRedirectForRole, isRoleAdmittedByRouteMeta }
